/**
 * "Vous étiez 4 à table — qui a aimé ?"
 *
 * The one place the household answers for a recipe it just ate. Every cook
 * gesture opens this same sheet (see VerdictPromptProvider): finishing cook
 * mode, logging a cook from recipe detail, and tapping the stars on a cooked
 * card. Those three used to rate three slightly different ways, which is what
 * #512 was.
 *
 * Answers are written on tap, so there is nothing to lose by dismissing. The
 * footer button is therefore a door, not a commit — it says "later" until you
 * have answered something and "done" afterwards, the same honest pattern
 * CookingDoneScreen already uses for its skip button.
 */
import React from 'react';
import { View, Text, Modal, Pressable, ScrollView, StyleSheet, Platform } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
  PaprikaShadows,
} from '../../styles/theme';
import { selectRecipeVerdicts } from '../../redux/recipeMetadataSlice';
import type { RootState } from '../../redux/store';
import { useHouseholdEaters } from '../../hooks/useHouseholdEaters';
import { VerdictRows, type VerdictSurface } from './VerdictRows';

const isWeb = Platform.OS === 'web';

interface Props {
  visible: boolean;
  recipeId: string;
  recipeTitle: string;
  surface: VerdictSurface;
  onClose: () => void;
  /** Roster shortcut, shown when nobody but the viewer is at the table yet. */
  onAddPeople?: () => void;
}

export function HouseholdVerdictSheet({
  visible,
  recipeId,
  recipeTitle,
  surface,
  onClose,
  onAddPeople,
}: Props) {
  const { t } = useTranslation();
  const { table } = useHouseholdEaters(t('household.eaters.selfLabel'));
  const verdicts = useSelector((s: RootState) => selectRecipeVerdicts(s, recipeId));
  const answered = Object.keys(verdicts).length > 0;

  return (
    <Modal
      visible={visible}
      transparent
      animationType={isWeb ? 'fade' : 'slide'}
      onRequestClose={onClose}
    >
      <Pressable style={isWeb ? styles.overlayWeb : styles.overlay} onPress={onClose}>
        <Pressable style={isWeb ? styles.sheetWeb : styles.sheet} onPress={(e) => e.stopPropagation()}>
          {!isWeb && (
            <View style={styles.handleWrap}>
              <View style={styles.handle} />
            </View>
          )}

          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.content}>
            <View style={styles.titleRow}>
              <Feather name="heart" size={15} color={Paprika.accent} />
              <Text style={styles.title}>
                {t('household.verdict.title', { count: table.length })}
              </Text>
            </View>

            <Text style={styles.question}>
              {t('household.verdict.question', { title: recipeTitle })}
            </Text>

            <VerdictRows recipeId={recipeId} surface={surface} />

            {table.length <= 1 && onAddPeople && (
              <Pressable style={styles.addPeople} onPress={onAddPeople}>
                <Feather name="user-plus" size={14} color={Paprika.accent} />
                <Text style={styles.addPeopleText}>{t('household.verdict.addPeople')}</Text>
              </Pressable>
            )}
            {table.length <= 1 && (
              <Text style={styles.soloHint}>{t('household.verdict.soloHint')}</Text>
            )}

            <Pressable
              style={[styles.footerButton, answered && styles.footerButtonDone]}
              onPress={onClose}
              accessibilityRole="button"
            >
              <Text style={[styles.footerText, answered && styles.footerTextDone]}>
                {answered ? t('household.verdict.done') : t('household.verdict.later')}
              </Text>
            </Pressable>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(27, 28, 26, 0.35)',
    justifyContent: 'flex-end',
  },
  overlayWeb: {
    flex: 1,
    backgroundColor: 'rgba(27, 28, 26, 0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sheet: {
    backgroundColor: Paprika.surfaceLowest,
    borderTopLeftRadius: PaprikaRadius.xl,
    borderTopRightRadius: PaprikaRadius.xl,
    maxHeight: '85%',
  },
  sheetWeb: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.lg,
    width: 420,
    maxWidth: '92%',
    maxHeight: '85%',
    ...PaprikaShadows.card,
  },
  handleWrap: {
    alignItems: 'center',
    paddingTop: PaprikaSpacing.sm,
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: Paprika.border,
  },
  content: {
    padding: PaprikaSpacing.md,
    gap: PaprikaSpacing.sm,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  title: {
    ...PaprikaTypography.titleMedium,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  question: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.sm,
  },
  addPeople: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    paddingVertical: 8,
  },
  addPeopleText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.accent,
  },
  soloHint: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
  },
  footerButton: {
    marginTop: PaprikaSpacing.sm,
    paddingVertical: 14,
    borderRadius: PaprikaRadius.lg,
    alignItems: 'center',
    backgroundColor: Paprika.surfaceLow,
  },
  footerButtonDone: {
    backgroundColor: Paprika.accent,
  },
  footerText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textSecondary,
  },
  footerTextDone: {
    color: '#ffffff',
  },
});
